import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FramerButton, TextInfoGroup } from "@/components/shared";
import { CustomerPageModals } from "@/constants/enums";
import { CustomerOnServiceProvider } from "@/graphql/__generated__/graphql-operations";
import { Dispatch, JSX, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

type Props = {
  showModal: CustomerPageModals;
  setShowModal: Dispatch<SetStateAction<CustomerPageModals>>;
  currentCustomer: CustomerOnServiceProvider;
}

/**
 * A modal component that displays the details of a Customer
 * @param {Props} props
 * @returns {JSX.Element} CustomerDetailsModal component
 */
function CustomerDetailsModal({showModal, setShowModal, currentCustomer }: Props): JSX.Element {

  // Hooks
  const { t } = useTranslation(["modals"]);
  const navigate = useNavigate();

  /**
   * OnClick handler for the History button
   */
  const handleHistoryClick = () => {
    // Close the modal
    setShowModal(CustomerPageModals.NoModal);
    // Navigate to CLR page
    navigate(`/user/creditlimitrequests?customerID=${currentCustomer._id}&customerName=${currentCustomer.name}`);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowModal(CustomerPageModals.NoModal);
    }
  };

  return (
    <Dialog open={showModal === CustomerPageModals.CustomerDetails} onOpenChange={handleOpenChange}>
      <DialogContent className="px-6 max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-mfdarklight"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="dark:text-white">{t("customerdetails.title")}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          <TextInfoGroup label={t("customerdetails.labels.name")} content={currentCustomer.name} />
          <TextInfoGroup label={t("customerdetails.labels.documentaryID")} content={currentCustomer.idFromDocumentary} />
        </div>
        <TextInfoGroup label={t("customerdetails.labels.publickey")} content={currentCustomer.publickey} />

        <DialogFooter>
          {/* Action Buttons */}
          <div className="mt-4 flex items-center justify-between w-full">
            <FramerButton
              onClick={() => {
                setShowModal(CustomerPageModals.NoModal);
              }}
              variant="outline-primary"
            >
              {t("common:buttons.close")}
            </FramerButton>

            <FramerButton
              onClick={handleHistoryClick}
              className="bg-mforange text-white hover:bg-mforange/80"
            >
              {t("customerdetails.buttons.history")}
            </FramerButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CustomerDetailsModal;
